import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Users, UserPlus, Sparkles, MapPin, Music, Crown, 
  Zap, ChevronDown, ChevronUp, RefreshCw, TrendingUp,
  Star, Target, Globe, Award
} from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Tier Badge Component
const TierBadge = ({ tier, small = false }) => {
  const colors = {
    elite: { bg: 'rgba(212, 175, 55, 0.2)', color: '#D4AF37', border: '1px solid #D4AF37' },
    pro: { bg: 'rgba(59, 130, 246, 0.2)', color: '#3b82f6', border: '1px solid #3b82f6' },
    starter: { bg: 'rgba(107, 114, 128, 0.2)', color: '#6b7280', border: '1px solid #6b7280' }
  };
  const style = colors[tier] || colors.starter;
  
  return (
    <span 
      className={`${small ? 'text-[10px] px-1.5 py-0.5' : 'text-xs px-2 py-0.5'} rounded-full font-medium`}
      style={{ background: style.bg, color: style.color, border: style.border }}
    >
      {tier?.toUpperCase()}
    </span>
  );
};

// Match Score Badge
const MatchScore = ({ percentage }) => {
  const getColor = () => {
    if (percentage >= 70) return '#22c55e';
    if (percentage >= 50) return '#D4AF37';
    if (percentage >= 30) return '#3b82f6';
    return '#6b7280';
  };
  
  return (
    <div className="flex items-center gap-1">
      <div 
        className="w-8 h-1.5 rounded-full overflow-hidden"
        style={{ background: 'rgba(255,255,255,0.1)' }}
      >
        <div 
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${percentage}%`, background: getColor() }}
        />
      </div>
      <span className="text-[10px]" style={{ color: getColor() }}>{percentage}%</span>
    </div>
  );
};

// Category Tab Button
const CategoryTab = ({ id, label, icon: Icon, active, onClick }) => (
  <button
    onClick={() => onClick(id)}
    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
      active 
        ? 'bg-[#D4AF37]/20 text-[#D4AF37] border border-[#D4AF37]' 
        : 'bg-white/5 border border-gray-700 hover:border-gray-500'
    }`}
    data-testid={`suggestion-tab-${id}`}
  >
    <Icon size={12} />
    {label}
  </button>
);

// Suggestion Card
const SuggestionCard = ({ suggestion, onFollow, isLoading }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <div 
      className="p-3 rounded-xl transition-all"
      style={{ 
        background: isHovered ? 'rgba(212, 175, 55, 0.05)' : 'rgba(255,255,255,0.02)',
        border: '1px solid rgba(255,255,255,0.1)'
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      data-testid={`suggestion-card-${suggestion.id}`}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div 
            className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ${
              suggestion.membership_tier === 'elite' ? 'ring-2 ring-yellow-500' : ''
            }`}
            style={{ background: 'rgba(212, 175, 55, 0.1)', color: '#D4AF37' }}
          >
            {(suggestion.dj_name || suggestion.name || 'D').charAt(0)}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <p className="font-medium text-sm">{suggestion.dj_name || suggestion.name}</p>
              <TierBadge tier={suggestion.membership_tier} small />
            </div>
            <p className="text-xs text-gray-400 mt-0.5">{suggestion.reason}</p>
          </div>
        </div>
        
        <Button
          size="sm"
          onClick={() => onFollow(suggestion.id)}
          disabled={isLoading}
          className="rounded-full h-7 px-3"
          style={{ background: '#D4AF37', color: '#000' }}
        >
          <UserPlus size={12} className="mr-1" />
          Follow
        </Button>
      </div>
      
      {/* Additional Info Row */}
      <div className="flex items-center gap-3 mt-3 text-xs text-gray-400">
        {suggestion.city && (
          <span className="flex items-center gap-1">
            <MapPin size={10} />
            {suggestion.city}
          </span>
        )}
        {suggestion.genres?.length > 0 && (
          <span className="flex items-center gap-1">
            <Music size={10} />
            {suggestion.genres.slice(0, 2).join(', ')}
          </span>
        )}
        {suggestion.mutual_friends > 0 && (
          <span className="flex items-center gap-1 text-green-400">
            <Users size={10} />
            {suggestion.mutual_friends} mutual
          </span>
        )}
        {suggestion.follows_you && (
          <span className="flex items-center gap-1 text-blue-400">
            <Star size={10} />
            Follows you
          </span>
        )}
      </div>
      
      {/* Match Score */}
      <div className="flex items-center justify-between mt-3 pt-2 border-t border-white/5">
        <span className="text-[10px] text-gray-500">Compatibility</span>
        <MatchScore percentage={suggestion.match_percentage} />
      </div>
    </div>
  );
};

// Network Insights Panel
const NetworkInsights = ({ insights, onClose }) => {
  if (!insights) return null;
  
  const { network_stats, network_score, composition, insights: insightsList } = insights;
  
  return (
    <div className="glass-panel rounded-xl p-4 border mb-4" style={{ borderColor: 'rgba(212, 175, 55, 0.3)' }}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold flex items-center gap-2" style={{ color: '#D4AF37' }}>
          <TrendingUp size={16} />
          Network Health
        </h3>
        <div className="flex items-center gap-2">
          <div 
            className="text-lg font-bold px-2 py-0.5 rounded-lg"
            style={{ 
              background: network_score >= 70 ? 'rgba(34, 197, 94, 0.2)' : network_score >= 40 ? 'rgba(212, 175, 55, 0.2)' : 'rgba(239, 68, 68, 0.2)',
              color: network_score >= 70 ? '#22c55e' : network_score >= 40 ? '#D4AF37' : '#ef4444'
            }}
          >
            {network_score}/100
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <ChevronUp size={16} />
          </button>
        </div>
      </div>
      
      {/* Stats Grid */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        <div className="p-2 rounded-lg bg-white/5 text-center">
          <p className="text-lg font-bold">{network_stats?.following_count || 0}</p>
          <p className="text-[10px] text-gray-400">Following</p>
        </div>
        <div className="p-2 rounded-lg bg-white/5 text-center">
          <p className="text-lg font-bold">{network_stats?.followers_count || 0}</p>
          <p className="text-[10px] text-gray-400">Followers</p>
        </div>
        <div className="p-2 rounded-lg bg-white/5 text-center">
          <p className="text-lg font-bold" style={{ color: '#22c55e' }}>{network_stats?.mutual_count || 0}</p>
          <p className="text-[10px] text-gray-400">Mutuals</p>
        </div>
      </div>
      
      {/* Insights List */}
      <div className="space-y-2">
        {insightsList?.slice(0, 3).map((insight, idx) => (
          <div 
            key={idx}
            className="flex items-start gap-2 text-xs p-2 rounded-lg"
            style={{ 
              background: insight.type === 'positive' ? 'rgba(34, 197, 94, 0.1)' : 'rgba(212, 175, 55, 0.1)'
            }}
          >
            {insight.type === 'positive' ? (
              <Star size={12} className="text-green-400 mt-0.5 flex-shrink-0" />
            ) : (
              <Target size={12} className="text-yellow-400 mt-0.5 flex-shrink-0" />
            )}
            <span style={{ color: insight.type === 'positive' ? '#86efac' : '#fde047' }}>
              {insight.message}
            </span>
          </div>
        ))}
      </div>
      
      {/* Top Genres */}
      {composition?.top_genres?.length > 0 && (
        <div className="mt-3 pt-3 border-t border-white/10">
          <p className="text-[10px] text-gray-400 mb-2">Top genres in your network</p>
          <div className="flex flex-wrap gap-1">
            {composition.top_genres.slice(0, 5).map(([genre, count]) => (
              <span 
                key={genre}
                className="text-[10px] px-2 py-0.5 rounded-full"
                style={{ background: 'rgba(212, 175, 55, 0.1)', color: '#D4AF37' }}
              >
                {genre} ({count})
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Main Component
const SuggestedConnections = ({ onFollowUpdate }) => {
  const { user } = useAuth();
  const [suggestions, setSuggestions] = useState([]);
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [activeCategory, setActiveCategory] = useState('all');
  const [showInsights, setShowInsights] = useState(false);
  const [totalAvailable, setTotalAvailable] = useState(0);
  
  const categories = [
    { id: 'all', label: 'Best Matches', icon: Sparkles },
    { id: 'genre', label: 'By Genre', icon: Music },
    { id: 'location', label: 'Nearby', icon: MapPin },
    { id: 'skill', label: 'Skill Level', icon: Award },
    { id: 'elite', label: 'Elite DJs', icon: Crown },
    { id: 'active', label: 'Most Active', icon: Zap }
  ];

  const fetchSuggestions = useCallback(async (category = 'all') => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/community/suggested-connections?category=${category}&limit=10`);
      setSuggestions(response.data.suggestions || []);
      setTotalAvailable(response.data.total_available || 0);
    } catch (error) {
      console.error('Failed to fetch suggestions:', error);
      // Fallback to legacy endpoint
      try {
        const legacyResponse = await axios.get(`${API}/community/suggested-djs`);
        setSuggestions(legacyResponse.data.map(dj => ({
          ...dj,
          match_percentage: 50,
          reason: 'Suggested DJ'
        })));
      } catch (err) {
        console.error('Legacy fallback also failed');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchInsights = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/community/connection-insights`);
      setInsights(response.data);
    } catch (error) {
      console.error('Failed to fetch insights:', error);
    }
  }, []);

  useEffect(() => {
    fetchSuggestions(activeCategory);
  }, [activeCategory, fetchSuggestions]);

  useEffect(() => {
    fetchInsights();
  }, [fetchInsights]);

  const handleFollow = async (userId) => {
    setActionLoading(true);
    try {
      const response = await axios.post(`${API}/community/follow/${userId}`);
      toast.success(response.data.following ? 'Following!' : 'Unfollowed');
      
      // Remove from suggestions
      setSuggestions(prev => prev.filter(s => s.id !== userId));
      
      // Refresh insights
      fetchInsights();
      
      // Notify parent component
      if (onFollowUpdate) onFollowUpdate();
    } catch (error) {
      toast.error('Failed to follow');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCategoryChange = (category) => {
    setActiveCategory(category);
  };

  const handleRefresh = () => {
    fetchSuggestions(activeCategory);
    fetchInsights();
  };

  return (
    <div className="space-y-4" data-testid="suggested-connections">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold flex items-center gap-2">
          <Users size={20} style={{ color: '#D4AF37' }} />
          Suggested Connections
        </h2>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowInsights(!showInsights)}
            className={`p-2 rounded-lg transition-colors ${showInsights ? 'bg-[#D4AF37]/20' : 'hover:bg-white/5'}`}
            title="Network Insights"
          >
            <TrendingUp size={16} style={{ color: showInsights ? '#D4AF37' : '#a3a3a3' }} />
          </button>
          <button
            onClick={handleRefresh}
            className="p-2 rounded-lg hover:bg-white/5 transition-colors"
            title="Refresh"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>
      
      {/* Network Insights Panel (Collapsible) */}
      {showInsights && (
        <NetworkInsights 
          insights={insights} 
          onClose={() => setShowInsights(false)} 
        />
      )}
      
      {/* Category Tabs */}
      <div className="flex flex-wrap gap-2">
        {categories.map(cat => (
          <CategoryTab
            key={cat.id}
            id={cat.id}
            label={cat.label}
            icon={cat.icon}
            active={activeCategory === cat.id}
            onClick={handleCategoryChange}
          />
        ))}
      </div>
      
      {/* Results Count */}
      {!loading && (
        <p className="text-xs text-gray-400">
          {suggestions.length > 0 
            ? `Showing ${suggestions.length} of ${totalAvailable} suggestions`
            : 'No suggestions found in this category'
          }
        </p>
      )}
      
      {/* Suggestions List */}
      <div className="space-y-3">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw size={24} className="animate-spin" style={{ color: '#D4AF37' }} />
          </div>
        ) : suggestions.length === 0 ? (
          <div className="text-center py-8">
            <Globe size={40} className="mx-auto mb-3 opacity-50" />
            <p className="text-gray-400 text-sm">No suggestions in this category</p>
            <p className="text-gray-500 text-xs mt-1">Try another category or expand your network</p>
          </div>
        ) : (
          suggestions.map(suggestion => (
            <SuggestionCard
              key={suggestion.id}
              suggestion={suggestion}
              onFollow={handleFollow}
              isLoading={actionLoading}
            />
          ))
        )}
      </div>
      
      {/* Load More (if available) */}
      {!loading && suggestions.length > 0 && totalAvailable > suggestions.length && (
        <button
          onClick={handleRefresh}
          className="w-full py-2 text-sm text-center rounded-lg border border-gray-700 hover:border-[#D4AF37] transition-colors"
        >
          Load more suggestions
        </button>
      )}
    </div>
  );
};

export default SuggestedConnections;
